<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { Globals } from '@/modules/Globals';
  import { useUi } from '@/composition/use-ui';
  import { useModal } from '@/composition/use-modal';
  import { useSupportReport } from '@/composition/use-support-report';
  import { AlertType } from '@/interfaces/IAlert';
  import { Capacitor } from '@capacitor/core';
  import { ILogicalPrinter, PrinterCutMode } from '@/stores/printing-v2.config';
  import {
    printerStatusUi,
    transportLabelKey,
    useV2PrintingUi,
  } from '@/composition/printing/use-printing-v2-ui';
  import { CODEPAGE_CYCLE, buildCodepageSample } from '@/lib/print/test-ticket';
  import { IPrinter } from '@/interfaces/IPrinter';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiListbox, {
    IPosUiListboxItem,
  } from '@/components/pos-v2/ui/PosUiListbox.vue';
  import V2PrinterSetupWizard from './wizard/V2PrinterSetupWizard.vue';

  export type IV2HelpFlow =
    | 'noprint'
    | 'symbols'
    | 'cut'
    | 'netfind'
    | 'noconnect';

  const props = defineProps<{
    /** Flow to auto-open on mount (e.g. jumping here from the offline hint). */
    initialFlow?: IV2HelpFlow | null;
  }>();

  const ui = useUi();
  const modals = useModal();
  const {
    v2Store,
    bridge,
    restaurantStore,
    printing,
    printingStore,
    isConnected,
    canEdit,
    logicalById,
    bridgePrinterByRef,
    printTestDocument,
  } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const downloadUrl = `${Globals.uploadsPath}/plugin2/QueRestoPrintBridge-Setup.exe`;

  const openFlow = ref<IV2HelpFlow | null>(props.initialFlow ?? null);

  function toggleFlow(flow: IV2HelpFlow) {
    openFlow.value = openFlow.value === flow ? null : flow;
  }

  const allPrinterItems = computed<IPosUiListboxItem[]>(() =>
    (v2Store.displayConfig?.logicalPrinters ?? []).map((lp) => ({
      id: lp.id,
      label: lp.name,
    }))
  );

  const escposPrinterItems = computed<IPosUiListboxItem[]>(() =>
    (v2Store.displayConfig?.logicalPrinters ?? [])
      .filter((lp) => lp.format === 'escpos')
      .map((lp) => ({ id: lp.id, label: lp.name }))
  );

  function logicalOf(id: number | string | null): ILogicalPrinter | null {
    if (id === null) return null;
    return (
      (v2Store.displayConfig?.logicalPrinters ?? []).find(
        (lp) => lp.id === Number(id)
      ) ?? null
    );
  }

  // #region flow 1: no imprime nada

  const noPrintPrinterId = ref<number | string | null>(null);
  const probing = ref(false);
  const probeResult = ref<'ok' | 'fail' | null>(null);
  const testingNoPrint = ref(false);

  async function probeNoPrint() {
    const logical = logicalOf(noPrintPrinterId.value);
    if (!logical || !bridge.value) return;
    probing.value = true;
    probeResult.value = null;
    try {
      const result = await bridge.value.probePrinter(logical.ref);
      probeResult.value = result.status === 'online' ? 'ok' : 'fail';
    } catch (e) {
      probeResult.value = 'fail';
    } finally {
      probing.value = false;
    }
  }

  async function testNoPrint() {
    const logical = logicalOf(noPrintPrinterId.value);
    if (!logical) return;
    testingNoPrint.value = true;
    try {
      await printTestDocument(logical, i18nT('printingV2.kindTest'));
      ui.showToast(AlertType.Success, i18nT('printingV2.testSent'));
    } catch (e) {
      ui.showToast(AlertType.Error, i18nT('printingV2.testFailed'));
    } finally {
      testingNoPrint.value = false;
    }
  }

  // #endregion

  // #region flow 2: símbolos raros (codepage cycle)

  const symbolsPrinterId = ref<number | string | null>(null);
  const printingSamples = ref(false);
  const samplesPrinted = ref(false);
  const savingCodepage = ref(false);

  async function printSamples() {
    const logical = logicalOf(symbolsPrinterId.value);
    if (!logical) return;
    printingSamples.value = true;
    try {
      const printer: IPrinter = {
        id: logical.ref,
        name: logical.name,
        type: 'usb',
      };
      // The printer's cut mode, not the 'modern' default: on a legacy-cut
      // printer the samples must still separate.
      const { escpos } = printingStore.printerSettingsOf(printer);
      for (let i = 0; i < CODEPAGE_CYCLE.length; i++) {
        const bytes = buildCodepageSample(
          i + 1,
          CODEPAGE_CYCLE[i],
          escpos.cutMode
        );
        await printing.printByteArray(printer, bytes, {
          kind: 'test',
          label: `${i18nT('printingV2.kindTest')} ${i + 1}`,
        });
      }
      samplesPrinted.value = true;
      ui.showToast(AlertType.Success, i18nT('printingV2.helpSamplesSent'));
    } catch (e) {
      console.error('[PrintingV2] codepage samples failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.testFailed'));
    } finally {
      printingSamples.value = false;
    }
  }

  async function pickSample(index: number) {
    const logical = logicalOf(symbolsPrinterId.value);
    if (!logical) return;
    savingCodepage.value = true;
    try {
      const codepage = CODEPAGE_CYCLE[index];
      const committed = await v2Store.mutateConfig((draft) => {
        const target = draft.logicalPrinters.find((lp) => lp.id === logical.id);
        if (target) target.codepage = codepage;
      });
      if (committed) {
        samplesPrinted.value = false;
        ui.showToast(AlertType.Success, i18nT('printingV2.helpCodepageSaved'));
      }
    } finally {
      savingCodepage.value = false;
    }
  }

  // #endregion

  // #region flow 3: corta mal

  const cutPrinterId = ref<number | string | null>(null);
  const testingCut = ref(false);
  const switchingCutMode = ref(false);

  const cutLogical = computed(() => logicalOf(cutPrinterId.value));

  const cutModeLabelKey = computed(() =>
    (cutLogical.value?.cutMode ?? 'modern') === 'legacy'
      ? 'printingV2.helpCutModeLegacy'
      : 'printingV2.helpCutModeModern'
  );

  async function testCut() {
    const logical = logicalOf(cutPrinterId.value);
    if (!logical) return;
    testingCut.value = true;
    try {
      await printTestDocument(logical, i18nT('printingV2.kindTest'));
      ui.showToast(AlertType.Success, i18nT('printingV2.testSent'));
    } catch (e) {
      ui.showToast(AlertType.Error, i18nT('printingV2.testFailed'));
    } finally {
      testingCut.value = false;
    }
  }

  /** Flips the printer's cut mode, saves it and prints a test with it. */
  async function switchCutModeAndTest() {
    const logical = logicalOf(cutPrinterId.value);
    if (!logical) return;
    const nextMode: PrinterCutMode =
      (logical.cutMode ?? 'modern') === 'modern' ? 'legacy' : 'modern';
    switchingCutMode.value = true;
    try {
      const committed = await v2Store.mutateConfig((draft) => {
        const target = draft.logicalPrinters.find((lp) => lp.id === logical.id);
        if (target) target.cutMode = nextMode;
      });
      if (!committed) return;
      // Explicit override: don't depend on the commit → projection timing
      // for the mode this very test is supposed to demonstrate.
      await printTestDocument(logical, i18nT('printingV2.kindTest'), {
        escpos: { cutMode: nextMode },
      });
      ui.showToast(AlertType.Success, i18nT('printingV2.helpCutModeSwitched'));
    } catch (e) {
      ui.showToast(AlertType.Error, i18nT('printingV2.testFailed'));
    } finally {
      switchingCutMode.value = false;
    }
  }

  // #endregion

  // #region flow: no encuentro mi impresora de red (netfind)

  function openWizardFromHelp() {
    modals.show(
      V2PrinterSetupWizard,
      { preTarget: null },
      {
        adaptive: true,
        height: 'auto',
        width: 720,
        maxWidth: window.innerWidth * 0.95,
        scrollable: true,
        name: 'v2-printer-setup-wizard',
      },
      {}
    );
  }

  // #endregion

  // #region diagnostics block (read-only, from existing stores)

  const diagnosticsOpen = ref(false);

  const identity = computed(() => bridge.value?.identity.value ?? null);
  const bridgePort = computed(() => bridge.value?.port ?? null);

  interface IDiagPrinterLine {
    key: string;
    name: string;
    transportKey: string;
    statusKey: string;
    statusColor: 'success' | 'error' | 'warning' | 'neutral';
  }

  const diagPrinters = computed<IDiagPrinterLine[]>(() =>
    (v2Store.displayConfig?.logicalPrinters ?? []).map((lp) => {
      const status = printerStatusUi(bridgePrinterByRef.value.get(lp.ref));
      return {
        key: `lp:${lp.id}`,
        name: lp.name,
        transportKey: transportLabelKey(lp.transport),
        statusKey: status.labelKey,
        statusColor: status.color,
      };
    })
  );

  interface IDiagSlotLine {
    key: string;
    slotLabel: string;
    printerName: string | null;
    statusKey: string | null;
    statusColor: 'success' | 'error' | 'warning' | 'neutral';
  }

  function diagSlotOf(
    key: string,
    slotLabel: string,
    logicalId: number | null
  ): IDiagSlotLine {
    const logical = logicalId !== null ? logicalById.value.get(logicalId) : null;
    if (!logical) {
      return {
        key,
        slotLabel,
        printerName: null,
        statusKey: null,
        statusColor: 'neutral',
      };
    }
    const status = printerStatusUi(bridgePrinterByRef.value.get(logical.ref));
    return {
      key,
      slotLabel,
      printerName: logical.name,
      statusKey: status.labelKey,
      statusColor: status.color,
    };
  }

  const diagSlots = computed<IDiagSlotLine[]>(() => {
    const assignments = v2Store.displayConfig?.assignments ?? {
      ticket: null,
      sticker: null,
      comandaByStation: {} as Record<number, number | null>,
    };
    const lines: IDiagSlotLine[] = [
      diagSlotOf('ticket', i18nT('printingV2.destTicket'), assignments.ticket),
    ];
    for (const station of restaurantStore.printStations) {
      lines.push(
        diagSlotOf(
          `comanda:${station.stationId}`,
          station.name,
          assignments.comandaByStation[station.stationId] ?? null
        )
      );
    }
    lines.push(
      diagSlotOf(
        'sticker',
        i18nT('printingV2.destSticker'),
        assignments.sticker
      )
    );
    return lines;
  });

  // #endregion

  // #region support report

  const supportReport = useSupportReport();
  const isNativeShare = Capacitor.isNativePlatform();

  // #endregion
</script>

<template>
  <div class="v2-help-tab">
    <!-- 1. No imprime nada -->
    <div class="help-flow" :class="{ open: openFlow === 'noprint' }">
      <button
        class="help-flow-header"
        type="button"
        @click="toggleFlow('noprint')"
      >
        <PosUiIcon name="printer-off" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.helpNoPrintTitle')
        }}</span>
        <PosUiIcon
          :name="openFlow === 'noprint' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'noprint'" class="help-flow-body">
        <ol class="help-steps">
          <li>{{ $t('printingV2.helpNoPrintStep1') }}</li>
          <li>{{ $t('printingV2.helpNoPrintStep2') }}</li>
          <li>{{ $t('printingV2.helpNoPrintStep3') }}</li>
        </ol>
        <div class="help-controls">
          <PosUiListbox
            :value="noPrintPrinterId"
            :items="allPrinterItems"
            :placeholder="$t('printingV2.helpSelectPrinter')"
            class="help-printer-select"
            @input="
              noPrintPrinterId = $event;
              probeResult = null;
            "
          />
          <PosUiButton
            color="neutral"
            variant="outline"
            size="sm"
            :loading="probing"
            :disabled="!isConnected || noPrintPrinterId === null"
            @click="probeNoPrint"
          >
            {{ $t('printingV2.helpProbe') }}
          </PosUiButton>
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            :loading="testingNoPrint"
            :disabled="!isConnected || noPrintPrinterId === null"
            @click="testNoPrint"
          >
            {{ $t('printingV2.testPrintButton') }}
          </PosUiButton>
        </div>
        <div v-if="probeResult === 'ok'" class="help-result is-ok">
          <PosUiIcon name="check-circle" class="help-result-icon" />
          {{ $t('printingV2.helpProbeOk') }}
        </div>
        <div v-else-if="probeResult === 'fail'" class="help-result is-fail">
          <PosUiIcon name="close-circle" class="help-result-icon" />
          {{ $t('printingV2.helpProbeFail') }}
        </div>
      </div>
    </div>

    <!-- 2. Imprime símbolos raros -->
    <div class="help-flow" :class="{ open: openFlow === 'symbols' }">
      <button
        class="help-flow-header"
        type="button"
        @click="toggleFlow('symbols')"
      >
        <PosUiIcon name="format-letter-case" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.helpSymbolsTitle')
        }}</span>
        <PosUiIcon
          :name="openFlow === 'symbols' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'symbols'" class="help-flow-body">
        <p class="help-text">{{ $t('printingV2.helpSymbolsIntro') }}</p>
        <div class="help-controls">
          <PosUiListbox
            :value="symbolsPrinterId"
            :items="escposPrinterItems"
            :placeholder="$t('printingV2.helpSelectPrinter')"
            class="help-printer-select"
            @input="
              symbolsPrinterId = $event;
              samplesPrinted = false;
            "
          />
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            :loading="printingSamples"
            :disabled="!isConnected || symbolsPrinterId === null"
            @click="printSamples"
          >
            {{ $t('printingV2.helpPrintSamples') }}
          </PosUiButton>
        </div>
        <template v-if="samplesPrinted">
          <p class="help-text">{{ $t('printingV2.helpPickSample') }}</p>
          <div class="help-controls">
            <PosUiButton
              v-for="(codepage, index) in CODEPAGE_CYCLE"
              :key="codepage"
              color="neutral"
              variant="outline"
              size="sm"
              :loading="savingCodepage"
              :disabled="!canEdit"
              @click="pickSample(index)"
            >
              {{ $t('printingV2.helpSampleN', { n: index + 1 }) }}
            </PosUiButton>
          </div>
        </template>
        <p class="help-footnote">{{ $t('printingV2.helpSymbolsMoreHint') }}</p>
      </div>
    </div>

    <!-- 3. Corta mal / no corta -->
    <div class="help-flow" :class="{ open: openFlow === 'cut' }">
      <button class="help-flow-header" type="button" @click="toggleFlow('cut')">
        <PosUiIcon name="content-cut" class="help-flow-icon" />
        <span class="help-flow-title">{{ $t('printingV2.helpCutTitle') }}</span>
        <PosUiIcon
          :name="openFlow === 'cut' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'cut'" class="help-flow-body">
        <p class="help-text">{{ $t('printingV2.helpCutBody') }}</p>
        <div class="help-controls">
          <PosUiListbox
            :value="cutPrinterId"
            :items="escposPrinterItems"
            :placeholder="$t('printingV2.helpSelectPrinter')"
            class="help-printer-select"
            @input="cutPrinterId = $event"
          />
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            :loading="testingCut"
            :disabled="!isConnected || cutPrinterId === null"
            @click="testCut"
          >
            {{ $t('printingV2.testPrintButton') }}
          </PosUiButton>
        </div>
        <template v-if="cutLogical">
          <p class="help-text">{{ $t('printingV2.helpCutSwitchIntro') }}</p>
          <div class="help-controls">
            <span class="help-cut-mode">
              {{ $t('printingV2.helpCutModeLabel') }}
              <strong>{{ $t(cutModeLabelKey) }}</strong>
            </span>
            <PosUiButton
              color="neutral"
              variant="outline"
              size="sm"
              :loading="switchingCutMode"
              :disabled="!canEdit"
              @click="switchCutModeAndTest"
            >
              {{ $t('printingV2.helpCutSwitchButton') }}
            </PosUiButton>
          </div>
        </template>
      </div>
    </div>

    <!-- 4. No encuentro mi impresora de red -->
    <div class="help-flow" :class="{ open: openFlow === 'netfind' }">
      <button
        class="help-flow-header"
        type="button"
        @click="toggleFlow('netfind')"
      >
        <PosUiIcon name="lan-pending" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.helpNetfindTitle')
        }}</span>
        <PosUiIcon
          :name="openFlow === 'netfind' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'netfind'" class="help-flow-body">
        <ol class="help-steps">
          <li>{{ $t('printingV2.helpNetfindStep1') }}</li>
          <li>{{ $t('printingV2.helpNetfindStep2') }}</li>
          <li>{{ $t('printingV2.helpNetfindStep3') }}</li>
        </ol>
        <div class="help-note">
          <PosUiIcon name="lightbulb-outline" class="help-note-icon" />
          {{ $t('printingV2.helpNetfindSelfTestTip') }}
        </div>
        <PosUiButton
          color="primary"
          variant="soft"
          size="sm"
          icon="plus"
          :disabled="!canEdit"
          @click="openWizardFromHelp"
        >
          {{ $t('printingV2.helpNetfindOpenWizard') }}
        </PosUiButton>
      </div>
    </div>

    <!-- 5. El plugin no conecta -->
    <div class="help-flow" :class="{ open: openFlow === 'noconnect' }">
      <button
        class="help-flow-header"
        type="button"
        @click="toggleFlow('noconnect')"
      >
        <PosUiIcon name="connection" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.helpConnectTitle')
        }}</span>
        <PosUiIcon
          :name="openFlow === 'noconnect' ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="openFlow === 'noconnect'" class="help-flow-body">
        <ol class="help-steps">
          <li>{{ $t('printingV2.helpConnectStep1') }}</li>
          <li>{{ $t('printingV2.helpConnectStep2') }}</li>
          <li>{{ $t('printingV2.helpConnectStep3') }}</li>
        </ol>
        <div class="help-note">
          <PosUiIcon name="lan-connect" class="help-note-icon" />
          {{ $t('printingV2.lnaNote') }}
        </div>
        <a class="help-download" :href="downloadUrl" download>
          <PosUiButton color="primary" variant="soft" size="sm" icon="download">
            {{ $t('printingV2.downloadButton') }}
          </PosUiButton>
        </a>
      </div>
    </div>

    <!-- Diagnostics (read-only, collapsible) -->
    <div class="help-flow" :class="{ open: diagnosticsOpen }">
      <button
        class="help-flow-header"
        type="button"
        @click="diagnosticsOpen = !diagnosticsOpen"
      >
        <PosUiIcon name="stethoscope" class="help-flow-icon" />
        <span class="help-flow-title">{{
          $t('printingV2.diagnosticsTitle')
        }}</span>
        <PosUiIcon
          :name="diagnosticsOpen ? 'chevron-up' : 'chevron-down'"
          class="help-flow-chevron"
        />
      </button>
      <div v-if="diagnosticsOpen" class="help-flow-body">
        <div class="diag-block">
          <div class="diag-block-title">
            {{ $t('printingV2.diagBridge') }}
          </div>
          <div v-if="identity" class="diag-line">
            <span class="diag-line-main">{{ identity.instanceName }}</span>
            <span class="diag-line-detail">
              v{{ identity.version
              }}<template v-if="bridgePort !== null">
                · {{ $t('printingV2.diagPort', { port: bridgePort }) }}
              </template>
            </span>
          </div>
          <div v-else class="diag-line">
            <span class="diag-line-detail">{{
              $t('printingV2.disconnected')
            }}</span>
          </div>
        </div>

        <div class="diag-block">
          <div class="diag-block-title">
            {{ $t('printingV2.diagPrinters') }}
          </div>
          <div v-if="!diagPrinters.length" class="diag-line">
            <span class="diag-line-detail">{{
              $t('printingV2.diagNoPrinters')
            }}</span>
          </div>
          <div
            v-for="line in diagPrinters"
            :key="line.key"
            class="diag-line"
          >
            <span class="diag-line-main">{{ line.name }}</span>
            <span class="diag-line-detail">{{ $t(line.transportKey) }}</span>
            <PosUiBadge :color="line.statusColor" variant="soft" size="xs">
              {{ $t(line.statusKey) }}
            </PosUiBadge>
          </div>
        </div>

        <div class="diag-block">
          <div class="diag-block-title">
            {{ $t('printingV2.diagSlots') }}
          </div>
          <div v-for="line in diagSlots" :key="line.key" class="diag-line">
            <span class="diag-line-main">{{ line.slotLabel }}</span>
            <template v-if="line.printerName">
              <span class="diag-line-detail">→ {{ line.printerName }}</span>
              <PosUiBadge
                v-if="line.statusKey"
                :color="line.statusColor"
                variant="soft"
                size="xs"
              >
                {{ $t(line.statusKey) }}
              </PosUiBadge>
            </template>
            <span v-else class="diag-line-detail">{{
              $t('printingV2.healthUnassigned')
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Support report -->
    <div class="help-support">
      <div class="help-support-text">
        <span class="help-support-title">{{
          $t('printingV2.supportReportTitle')
        }}</span>
        <span class="help-support-body">{{
          $t('printingV2.supportReportBody')
        }}</span>
      </div>
      <div class="help-support-actions">
        <template v-if="isNativeShare">
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            icon="share-variant"
            :loading="supportReport.isBuilding.value"
            @click="supportReport.shareReport('printing_help')"
          >
            {{ $t('printingV2.supportReportShare') }}
          </PosUiButton>
        </template>
        <template v-else>
          <PosUiButton
            color="neutral"
            variant="outline"
            size="sm"
            icon="content-copy"
            :loading="supportReport.isBuilding.value"
            @click="supportReport.copyReport('printing_help')"
          >
            {{ $t('printingV2.supportReportCopy') }}
          </PosUiButton>
          <PosUiButton
            color="primary"
            variant="soft"
            size="sm"
            icon="download"
            :loading="supportReport.isBuilding.value"
            @click="supportReport.downloadReport('printing_help')"
          >
            {{ $t('printingV2.supportReportDownload') }}
          </PosUiButton>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-help-tab {
    @apply tw-flex tw-flex-col tw-gap-2;

    .help-support {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-dashed tw-border-neutral-200 tw-bg-neutral-50 tw-px-4 tw-py-3.5;

      .help-support-text {
        @apply tw-flex tw-min-w-[200px] tw-flex-1 tw-flex-col tw-gap-0.5;

        .help-support-title {
          @apply tw-text-sm tw-font-semibold tw-text-neutral-800;
        }

        .help-support-body {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }

      .help-support-actions {
        @apply tw-flex tw-flex-none tw-items-center tw-gap-2;
      }
    }

    .help-flow {
      @apply tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

      &.open {
        @apply tw-border-neutral-300;
      }

      .help-flow-header {
        @apply tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-left tw-transition-colors hover:tw-bg-neutral-50;

        .help-flow-icon {
          @apply tw-flex-none tw-text-xl tw-text-neutral-400;
        }

        .help-flow-title {
          @apply tw-flex-1 tw-text-sm tw-font-semibold tw-text-neutral-800;
        }

        .help-flow-chevron {
          @apply tw-text-lg tw-text-neutral-400;
        }
      }

      .help-flow-body {
        @apply tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-neutral-100 tw-px-4 tw-py-3.5;

        .help-text {
          @apply tw-m-0 tw-text-sm tw-text-neutral-600;
        }

        .help-steps {
          @apply tw-m-0 tw-flex tw-flex-col tw-gap-1.5 tw-pl-5 tw-text-sm tw-text-neutral-600;
        }

        .help-controls {
          @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

          .help-printer-select {
            @apply tw-max-w-[260px];
          }

          .help-cut-mode {
            @apply tw-text-sm tw-text-neutral-600;

            strong {
              @apply tw-font-semibold tw-text-neutral-800;
            }
          }
        }

        .help-result {
          @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm;

          &.is-ok {
            @apply tw-bg-emerald-50 tw-text-emerald-700;
          }

          &.is-fail {
            @apply tw-bg-red-50 tw-text-red-700;
          }

          .help-result-icon {
            @apply tw-text-base;
          }
        }

        .help-note {
          @apply tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-xs tw-text-neutral-500;

          .help-note-icon {
            @apply tw-mt-0.5 tw-flex-none tw-text-base tw-text-neutral-400;
          }
        }

        .help-footnote {
          @apply tw-m-0 tw-border-t tw-border-neutral-100 tw-pt-2.5 tw-text-xs tw-text-neutral-400;
        }

        .help-download {
          @apply tw-self-start tw-no-underline;
        }

        .diag-block {
          @apply tw-flex tw-flex-col tw-gap-1;

          .diag-block-title {
            @apply tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-400;
          }

          .diag-line {
            @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm;

            .diag-line-main {
              @apply tw-font-medium tw-text-neutral-800;
            }

            .diag-line-detail {
              @apply tw-text-xs tw-text-neutral-500;
            }
          }
        }
      }
    }
  }
</style>
